import { Card, Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Autocomplete, Box, Button, FormField, SelectMultiLanguage, Typography, useCommonComponentContext, IPropDetailContainer } from "@maysoft/common-component-react";

import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import TagService from "../../services/message/Tag.service";

import { Constants } from "../../constants/constants";
import { Mode, TagType } from "../../constants/enums";
import { ICodename, IMultiLanguageContent } from "../../commons/interfaces";


interface ITag {
    id?: string;
    type?: number;
    name?: IMultiLanguageContent;
    shortName?: IMultiLanguageContent;
    sequence?: number;
    updateTime?: any;
}

const TagEditContainer = (props: IPropDetailContainer) => {
    const Strings = useStrings();
    const { language, tenantCode, serviceCode, listMultiLanguage, onError, onSuccess, onHideLoading, onShowLoading } = useCommonComponentContext();

    const [isEdited, setIsEdited] = useState<boolean>(false);
    const [dataTag, setDataTag] = useState<ITag>({} as ITag);
    const [errors, setErrors] = useState<{ [key in keyof ITag]?: string }>({});
    const [selectedLanguage, setSelectedLanguage] = useState<string>(Constants.DefaultLanguage);

    const dataType: ICodename[] = [
        { code: TagType.Post, name: Strings.TAG.POST },
        { code: TagType.Course, name: Strings.TAG.COURSE },
        { code: TagType.Expertised, name: Strings.TAG.EXPERTISED },
    ];

    useEffect(() => {
        setSelectedLanguage(language);
    }, [language]);

    useEffect(() => {
        (async () => {
            try {
                onShowLoading();

                if (Helpers.isNullOrEmpty(props.idDetail)) {
                } else {
                    const result = await TagService.getById(props.idDetail);
                    setDataTag({
                        id: result.id,
                        type: result.type,
                        name: result.name,
                        shortName: result.shortName,
                        updateTime: result.updateTime,
                    });
                }
            } catch (error) {
                onHideLoading();
                const err = Helpers.renderExceptionError(error);
                onError(err);
            } finally {
                onHideLoading();
            }
        })();
    }, [props.idDetail]);

    const checkedValidate = () => {
        let isChecked = true;
        let dataErrTemp: any = { ...errors };

        if (Helpers.isNullOrEmpty(dataTag?.type)) {
            isChecked = false;
            dataErrTemp["type"] = Strings.Common.REQUIRED;
        }
        if (Helpers.isNullOrEmpty(dataTag?.name?.value?.[selectedLanguage])) {
            isChecked = false;
            dataErrTemp["name"] = Strings.Common.REQUIRED;
        }

        if (!isChecked) {
            setErrors(dataErrTemp);
        }

        return isChecked;
    };

    const handleCreateOrUpdate = async () => {
        if (props.mode === Mode.View) {
            props.onChangeMode(Mode.Update);
        } else {
            if (!checkedValidate()) {
                return;
            } else {
                try {
                    onShowLoading();

                    const dataSubmit: any = {
                        type: dataTag?.type,
                        tenantCode: tenantCode,
                        serviceCode: serviceCode,
                        name: Helpers.getDataMultiLanguage({
                            data: dataTag?.name,
                            currentLanguage: language,
                            listLanguage: listMultiLanguage,
                        }),
                        shortName: Helpers.getDataMultiLanguage({
                            data: dataTag?.shortName,
                            currentLanguage: language,
                            listLanguage: listMultiLanguage,
                        }),
                        sequence: dataTag?.sequence
                    };

                    if (Helpers.isNullOrEmpty(dataTag?.id)) {
                        const resultId = await TagService.create(dataSubmit);

                        setDataTag({ ...dataTag, id: resultId });
                        setIsEdited(false);

                        onSuccess(Strings.TAG.UPDATE_SUCCESS);

                        props.onGoBack();
                    } else {
                        await TagService.update({
                            ...dataSubmit,
                            id: dataTag?.id,
                            updateTime: dataTag.updateTime,
                        });

                        setIsEdited(false);
                        props.onChangeMode(Mode.View);

                        onSuccess(Strings.TAG.UPDATE_SUCCESS);
                    }
                } catch (error: any) {
                    onHideLoading();
                    const err = Helpers.renderExceptionError(error);
                    onError(err);
                } finally {
                    onHideLoading();
                }
            }
        }
    };

    const renderTagInfo = () => (
        <Card>
            <Box p={2}>
                <Box display="flex" justifyContent="space-between">
                    <Typography variant="h5">{Strings.TAG.TAG_INFO}</Typography>
                    <SelectMultiLanguage
                        defaultLanguage={language}
                        onChangeLanguage={(value) => {
                            setSelectedLanguage(value);
                            setDataTag({
                                ...dataTag,
                                name: Helpers.getDataMultiLanguage({
                                    data: dataTag?.name,
                                    currentLanguage: language,
                                    listLanguage: listMultiLanguage,
                                }),
                                shortName: Helpers.getDataMultiLanguage({
                                    data: dataTag?.shortName,
                                    currentLanguage: language,
                                    listLanguage: listMultiLanguage,
                                }),
                            });
                        }}
                    />
                </Box>
                <Grid container spacing={3} p={2}>
                    <Grid item xs={12} md={12}>
                        <Autocomplete
                            required
                            key={dataTag?.type?.toString()}
                            mode={props.mode}
                            data={dataType || []}
                            errorMessage={errors?.type}
                            defaultValue={!Helpers.isNullOrEmpty(dataTag?.type) ? dataTag.type : null}
                            label={Strings.TAG.TYPE_TAG}
                            placeholder={Strings.TAG.SELECT_TYPE_TAG}
                            onChange={(value: number) => {
                                setIsEdited(true);
                                setErrors((prev) => ({ ...prev, type: undefined }));
                                setDataTag((prev) => ({
                                    ...prev,
                                    type: value,
                                }));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormField
                            required
                            maxLength={255}
                            mode={props.mode}
                            label={Strings.TAG.TAG_NAME}
                            placeholder={Strings.TAG.ENTER_TAG_NAME}
                            defaultValue={dataTag?.name?.value?.[selectedLanguage]}
                            errorMessage={errors?.name}
                            onBlur={(value: string) => {
                                setIsEdited(true);
                                setErrors((prev) => ({ ...prev, name: undefined }));
                                setDataTag((prev) => ({
                                    ...prev,
                                    name: {
                                        ...prev.name,
                                        value: {
                                            ...prev.name?.value,
                                            [selectedLanguage]: value,
                                        },
                                    },
                                }));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormField
                            maxLength={255}
                            mode={props.mode}
                            label={Strings.TAG.SHORT_NAME}
                            placeholder={Strings.TAG.ENTER_SHORT_NAME}
                            defaultValue={dataTag?.shortName?.value?.[selectedLanguage]}
                            errorMessage={errors?.shortName}
                            onBlur={(value: string) => {
                                setIsEdited(true);
                                setErrors((prev) => ({ ...prev, shortName: undefined }));
                                setDataTag((prev) => ({
                                    ...prev,
                                    shortName: {
                                        ...prev.shortName,
                                        value: {
                                            ...prev.shortName?.value,
                                            [selectedLanguage]: value,
                                        },
                                    },
                                }));
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <FormField
                            type="number"
                            mode={props.mode}
                            label={Strings.TAG.SEQUENCE}
                            placeholder={Strings.TAG.SEQUENCE}
                            defaultValue={dataTag?.sequence}
                            onBlur={(value: string) => {
                                setIsEdited(true);
                                setDataTag((prev) => ({
                                    ...prev,
                                    sequence: parseInt(value),
                                }));
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
        </Card>
    );

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "end", mb: 3 }}>
                        <Button
                            color={"secondary"}
                            onClick={() => {
                                if (isEdited) {
                                    Helpers.showConfirmAlert(Strings.Common.CANCEL_ACTION_MESSAGE, props.onGoBack);
                                } else {
                                    props.onGoBack();
                                }
                            }}
                        >
                            {Strings.Common.GO_BACK}
                        </Button>
                        <Button
                            sx={{ ml: 2 }}
                            color={"dark"}
                            onClick={() => {
                                handleCreateOrUpdate();
                            }}
                        >
                            {props.mode === Mode.View ? Strings.Common.EDIT : Strings.Common.SAVE}
                        </Button>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    {renderTagInfo()}
                </Grid>
            </Grid>
        </Box>
    );
};

export default TagEditContainer;
